import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Plus,
  Calendar as CalendarIcon,
  List,
  RefreshCw,
  Settings,
  CalendarDays,
  CalendarClock,
  CheckCircle2,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import arLocale from '@fullcalendar/core/locales/ar';
import heLocale from '@fullcalendar/core/locales/he';
import { appointmentsApi, appointmentTypesApi, workersApi, schedulesApi } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import AppointmentModal from '../components/appointments/AppointmentModal';
import AppointmentList from '../components/appointments/AppointmentList';
import AppointmentSettings from '../components/appointments/AppointmentSettings';
import './AppointmentsPage.css';

const CALENDAR_LOCALES = [arLocale, heLocale];

// `datetime-local` inputs need a local (not UTC) "YYYY-MM-DDTHH:mm" string.
const toLocalInputValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const AppointmentsPage = () => {
  const { t } = useTranslation();
  const { isRTL, currentLanguage } = useLanguage();
  const calendarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [prefillCustomer, setPrefillCustomer] = useState(null);

  // State
  const [appointments, setAppointments] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Individual fetch functions for smart refresh
  const fetchTypes = useCallback(async () => {
    try {
      const res = await appointmentTypesApi.list();
      if (res.success) setAppointmentTypes(res.data || []);
    } catch (error) {
      console.error('Error fetching types:', error);
    }
  }, []);

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await workersApi.list();
      if (res.success) setWorkers(res.data || []);
    } catch (error) {
      console.error('Error fetching workers:', error);
    }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const res = await schedulesApi.list();
      if (res.success) setSchedules(res.data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, []);

  // Fetch all data (initial load)
  const fetchData = useCallback(async () => {
    try {
      const [aptsRes, typesRes, workersRes, schedulesRes] = await Promise.all([
        appointmentsApi.list(),
        appointmentTypesApi.list(),
        workersApi.list(),
        schedulesApi.list(),
      ]);

      if (aptsRes.success) setAppointments(aptsRes.data || []);
      if (typesRes.success) setAppointmentTypes(typesRes.data || []);
      if (workersRes.success) setWorkers(workersRes.data || []);
      if (schedulesRes.success) setSchedules(schedulesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Calendar event formatting
  const calendarEvents = useMemo(
    () =>
      appointments.map((apt) => {
        const aptType = appointmentTypes.find((type) => type.id === apt.appointment_type_id);
        const worker = workers.find((w) => w.id === apt.worker_id);

        let backgroundColor = 'var(--color-accent-primary)';
        if (apt.status === 'completed') backgroundColor = 'var(--color-success)';
        if (apt.status === 'cancelled') backgroundColor = 'var(--color-error)';

        const customerName = apt.name || t('appointments.customer');
        const typeName = aptType?.name || '';

        return {
          id: apt.id.toString(),
          title: typeName ? `${customerName} · ${typeName}` : customerName,
          start: apt.start_time,
          end: apt.end_time,
          backgroundColor,
          borderColor: backgroundColor,
          classNames: [`fc-status-${apt.status || 'scheduled'}`],
          extendedProps: {
            ...apt,
            appointmentType: aptType,
            worker,
            typeName,
            customerName: apt.name || '',
          },
        };
      }),
    [appointments, appointmentTypes, workers, t]
  );

  // Quick counts shown above the calendar
  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday.getTime() + 24 * 60 * 60 * 1000);

    let today = 0;
    let upcoming = 0;
    let completed = 0;

    appointments.forEach((apt) => {
      const start = new Date(apt.start_time);
      if (Number.isNaN(start.getTime())) return;
      if (apt.status === 'completed') completed += 1;
      if (start >= startOfToday && start < endOfToday) today += 1;
      else if (start >= endOfToday && apt.status !== 'cancelled') upcoming += 1;
    });

    return { today, upcoming, completed, total: appointments.length };
  }, [appointments]);

  // Custom event content renderer for day/week views
  const renderEventContent = (eventInfo) => {
    const { typeName, customerName, phone_number: phone } = eventInfo.event.extendedProps;

    return (
      <div className="fc-event-custom">
        <span className="fc-event-time">{eventInfo.timeText}</span>
        <span className="fc-event-title">{customerName || t('appointments.customer')}</span>
        {typeName && <span className="fc-event-type">{typeName}</span>}
        {phone && <span className="fc-event-meta">{phone}</span>}
      </div>
    );
  };

  // Calculate earliest appointment time for scroll position
  const earliestAppointmentTime = useMemo(() => {
    if (appointments.length === 0) return '08:00:00';

    const times = appointments.map((apt) => {
      const date = new Date(apt.start_time);
      return date.getHours() * 60 + date.getMinutes();
    });

    const earliest = Math.min(...times);
    const paddedMinutes = Math.max(0, earliest - 30);
    const hours = Math.floor(paddedMinutes / 60);
    const mins = paddedMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
  }, [appointments]);

  // Tags the lanes belonging to the current hour so day view can highlight just
  // that band (see globals.css) rather than shading the entire column.
  const slotLaneClassNames = (arg) => {
    const now = new Date();
    const { currentStart, currentEnd } = arg.view;
    if (!(currentStart <= now && now < currentEnd)) return [];

    const slotHour = arg.date instanceof Date ? arg.date.getHours() : arg.time?.hours;
    return slotHour === now.getHours() ? ['fc-slot-now'] : [];
  };

  const openCreateModal = (startValue) => {
    setSelectedAppointment(null);
    setSelectedDate(startValue);
    setShowModal(true);
  };

  // Open the create modal pre-filled when navigated here from a chat.
  useEffect(() => {
    const pre = location.state?.prefillCustomer;
    if (pre && (pre.name || pre.phone)) {
      setPrefillCustomer(pre);
      setSelectedAppointment(null);
      setSelectedDate(toLocalInputValue(new Date().setHours(9, 0, 0, 0)));
      setShowModal(true);
      // Clear nav state so a refresh/back doesn't reopen the modal.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Calendar handlers
  const handleDateClick = (arg) => {
    const calendarApi = calendarRef.current?.getApi();

    // Month view: a plain click drills into the day instead of creating.
    if (calendarApi && calendarApi.view.type === 'dayGridMonth') {
      calendarApi.changeView('timeGridDay', arg.dateStr);
      return;
    }

    // Time-grid views are handled by `select` so that click and drag behave
    // identically; bail out here to avoid opening the modal twice.
  };

  // Fires for click-and-drag range selection (and for single clicks in time
  // grid views). This is what makes dragging out a time slot open the form
  // pre-filled with the range the user drew.
  const handleSelect = (arg) => {
    const calendarApi = calendarRef.current?.getApi();
    const isMonthView = arg.view.type === 'dayGridMonth';
    // 25h of slack so a single day never counts as a multi-day drag across DST.
    const spansMultipleDays = arg.end - arg.start > 25 * 60 * 60 * 1000;

    if (isMonthView && !spansMultipleDays) {
      // Single-day click in month view: `dateClick` already drilled down.
      calendarApi?.unselect();
      return;
    }

    // All-day/month selections have no meaningful time, so default to 09:00.
    const start = new Date(arg.start);
    if (arg.allDay) start.setHours(9, 0, 0, 0);

    openCreateModal(toLocalInputValue(start));
    calendarApi?.unselect();
  };

  const handleEventClick = (arg) => {
    const apt = arg.event.extendedProps;
    setSelectedAppointment({
      ...apt,
      id: parseInt(arg.event.id, 10),
    });
    setSelectedDate(null);
    setShowModal(true);
  };

  // Create/Update handlers
  const handleSave = async (data) => {
    try {
      if (selectedAppointment) {
        const result = await appointmentsApi.update(selectedAppointment.id, data);
        if (result.success) {
          toast.success(t('appointments.updateSuccess'));
          fetchData();
        } else {
          toast.error(result.error || t('appointments.updateError'));
        }
      } else {
        const result = await appointmentsApi.create(data);
        if (result.success) {
          toast.success(t('appointments.createSuccess'));
          fetchData();
        } else {
          toast.error(result.error || t('appointments.createError'));
        }
      }
      setShowModal(false);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async () => {
    if (!selectedAppointment) return;

    try {
      const result = await appointmentsApi.delete(selectedAppointment.id);
      if (result.success) {
        toast.success(t('appointments.deleteSuccess'));
        fetchData();
        setShowModal(false);
      } else {
        toast.error(result.error || t('appointments.deleteError'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
    setSelectedDate(null);
    setPrefillCustomer(null);
  };

  // List item click
  const handleListItemClick = (apt) => {
    setSelectedAppointment(apt);
    setSelectedDate(null);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`appointments-page ${isRTL ? 'rtl' : ''}`}>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title-section">
          <h1 className="page-title">{t('appointments.title')}</h1>
          <p className="page-subtitle">{t('appointments.subtitle')}</p>
        </div>

        <div className="page-actions">
          {/* View Toggle */}
          <div className="view-toggle" role="group">
            <button
              type="button"
              className={`view-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
              title={t('appointments.viewCalendar')}
              aria-pressed={viewMode === 'calendar'}
            >
              <CalendarIcon size={18} />
            </button>
            <button
              type="button"
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title={t('appointments.viewList')}
              aria-pressed={viewMode === 'list'}
            >
              <List size={18} />
            </button>
          </div>

          <Button
            variant="secondary"
            icon={<Settings size={18} />}
            onClick={() => setShowSettings(true)}
            title={t('appointments.settings')}
          />

          <Button
            variant="secondary"
            icon={<RefreshCw size={18} className={refreshing ? 'spin' : ''} />}
            onClick={handleRefresh}
            disabled={refreshing}
            title={t('common.refresh', 'Refresh')}
          />

          <Button
            variant="primary"
            icon={<Plus size={18} />}
            onClick={() => openCreateModal(toLocalInputValue(new Date().setHours(9, 0, 0, 0)))}
          >
            {t('appointments.newAppointment')}
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="apt-stats">
        <div className="apt-stat">
          <span className="apt-stat-icon today">
            <CalendarDays size={18} />
          </span>
          <div className="apt-stat-copy">
            <strong>{stats.today}</strong>
            <span>{t('appointments.calendarToday')}</span>
          </div>
        </div>
        <div className="apt-stat">
          <span className="apt-stat-icon upcoming">
            <CalendarClock size={18} />
          </span>
          <div className="apt-stat-copy">
            <strong>{stats.upcoming}</strong>
            <span>{t('appointments.scheduled')}</span>
          </div>
        </div>
        <div className="apt-stat">
          <span className="apt-stat-icon done">
            <CheckCircle2 size={18} />
          </span>
          <div className="apt-stat-copy">
            <strong>{stats.completed}</strong>
            <span>{t('appointments.completed')}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {viewMode === 'calendar' ? (
          <div className="calendar-container card">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              buttonText={{
                today: t('appointments.calendarToday'),
                month: t('appointments.calendarMonth'),
                week: t('appointments.calendarWeek'),
                day: t('appointments.calendarDay'),
              }}
              locales={CALENDAR_LOCALES}
              locale={currentLanguage}
              direction={isRTL ? 'rtl' : 'ltr'}
              events={calendarEvents}
              eventContent={renderEventContent}
              dateClick={handleDateClick}
              select={handleSelect}
              eventClick={handleEventClick}
              editable={false}
              selectable
              selectMirror
              longPressDelay={250}
              selectLongPressDelay={250}
              dayMaxEvents={3}
              weekends
              height="auto"
              aspectRatio={1.8}
              scrollTime={earliestAppointmentTime}
              scrollTimeReset={false}
              slotMinTime="06:00:00"
              slotMaxTime="23:00:00"
              slotDuration="00:30:00"
              slotLaneClassNames={slotLaneClassNames}
              slotLabelFormat={{ hour: 'numeric', minute: '2-digit', omitZeroMinute: false }}
              expandRows
              stickyHeaderDates
              nowIndicator
            />
          </div>
        ) : (
          <AppointmentList
            appointments={appointments}
            appointmentTypes={appointmentTypes}
            workers={workers}
            onItemClick={handleListItemClick}
          />
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <AppointmentModal
          isOpen={showModal}
          onClose={handleCloseModal}
          appointment={selectedAppointment}
          appointmentTypes={appointmentTypes}
          workers={workers}
          defaultDate={selectedDate}
          defaultCustomer={prefillCustomer}
          onSave={handleSave}
          onDelete={selectedAppointment ? handleDelete : null}
        />
      )}

      {/* Settings Modal */}
      <Modal isOpen={showSettings} onClose={() => setShowSettings(false)} size="lg" hideHeader>
        <AppointmentSettings
          appointmentTypes={appointmentTypes}
          workers={workers}
          schedules={schedules}
          onRefreshTypes={fetchTypes}
          onRefreshWorkers={fetchWorkers}
          onRefreshSchedules={fetchSchedules}
          onClose={() => setShowSettings(false)}
        />
      </Modal>
    </div>
  );
};

export default AppointmentsPage;
